import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { DateRange, DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

type DateRangePickerFieldProps = {
  label?: string;
  value?: DateRange;
  onChange?: (value: DateRange) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  classNameContainer?: string;
  className?: string;
  name?: string;
  showClear?: boolean; // show an inline clear icon to reset value
};

/**
 * A lightweight date picker field styled to match SelectField.
 * It wraps a native input[type="date"] for broad browser support and simplicity.
 */
const DateRangePickerField: React.FC<DateRangePickerFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  errorMessage,
  classNameContainer,
  className = "w-full pr-10 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400",
  name,
  showClear,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const displayValue = useMemo(() => {
    if (!value) return "";
    const { from, to } = value;
    return `${format(from || new Date(), "yyyy-MM-dd")} - ${format(
      to || new Date(),
      "yyyy-MM-dd"
    )}`;
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    // Use pointerdown + capture to reliably detect outside clicks
    // even inside Radix Popover portals (Radix works with pointer events)
    document.addEventListener("pointerdown", handleClickOutside, true);
    return () => document.removeEventListener("pointerdown", handleClickOutside, true);
  }, [isOpen]);

  const getDropdownStyle = () => {
    if (!containerRef.current || !isOpen) return {} as React.CSSProperties;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const dropdownHeight = 356;
    const margin = 12;
    let left = rect.left;
    let top = rect.bottom + 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + dropdownWidth + margin > vw) left = vw - dropdownWidth - margin;
    if (top + dropdownHeight + margin > vh) top = rect.top - dropdownHeight - 8;
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    return { position: "fixed" as const, left, top, zIndex: 10000 };
  };

  const handleSelect = (d?: DateRange) => {
    if (!d || disabled) return;
    try {
      if (onChange) onChange(d);
    } catch {}
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (onChange) onChange(undefined);
  };

  return (
    <div className={`w-full ${classNameContainer || ""}`}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
      ) : null}
      <div ref={containerRef} className="relative">
        <input
          type="text"
          name={name}
          value={displayValue}
          onChange={() => {}}
          disabled={disabled}
          placeholder={placeholder || t("Select date")}
          className={className}
          onClick={() => !disabled && setIsOpen((o) => !o)}
          readOnly
        />
        {showClear && !!displayValue && !disabled ? (
          <button
            type="button"
            aria-label={t("Clear date")}
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XIcon className="w-4 h-4" />
          </button>
        ) : (
          <CalendarIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        )}
        {isOpen && (
          <div
            ref={dropdownRef}
            style={getDropdownStyle()}
            className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <DayPicker
              mode="range"
              selected={value}
              onSelect={handleSelect}
              showOutsideDays
              fixedWeeks
              className="p-2"
            />
          </div>
        )}
      </div>
      {errorMessage ? <p className="mt-1 text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
};

export default DateRangePickerField;
